import { Platform } from 'react-native';
import { normalizePublicDigestPreviewTrialPath } from './publicDigestPreviewPath';

const DEFAULT_BASE_URL = 'https://paperzilla.ai';
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:8001';

export interface PublicDigestPreviewAccess {
    digest_id: string;
    project_id: string;
    preview_access: 'restricted' | 'unrestricted';
    visible_project_paper_ids: string[];
    papers_total_count: number;
    hidden_count: number;
    trial_path: string | null;
}

export interface PublicProjectPreviewAccess {
    project_id: string;
    preview_access: 'restricted' | 'unrestricted';
    result_digest_id: string | null;
    visible_project_paper_ids: string[];
}

export class PublicProjectPreviewNotFoundError extends Error {
    constructor() {
        super('Project not found or unavailable.');
        this.name = 'PublicProjectPreviewNotFoundError';
    }
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function endpointCandidates(path: string): string[] {
    const configuredBase = trimTrailingSlash(
        process.env.EXPO_PUBLIC_BASE_URL || DEFAULT_BASE_URL
    );

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const isLocal = window.location.hostname === 'localhost'
            || window.location.hostname === '127.0.0.1';
        if (isLocal) {
            return [`${DEFAULT_LOCAL_API_BASE_URL}${path}`, path];
        }

        return [path];
    }

    return [`${configuredBase}${path}`];
}

function normalizePayload(payload: unknown, digestId: string): PublicDigestPreviewAccess {
    const value = payload as Partial<PublicDigestPreviewAccess> | null;
    const previewAccess = value?.preview_access;
    const visibleIds = Array.isArray(value?.visible_project_paper_ids)
        ? value.visible_project_paper_ids.filter((item): item is string => typeof item === 'string')
        : [];

    const normalizedTrialPath = previewAccess === 'restricted'
        && typeof value?.trial_path === 'string'
        && typeof value.project_id === 'string'
        ? normalizePublicDigestPreviewTrialPath({
            trialPath: value.trial_path,
            projectId: value.project_id,
            digestId,
        })
        : null;

    if (
        value?.digest_id !== digestId
        || typeof value.project_id !== 'string'
        || !value.project_id.trim()
        || (previewAccess !== 'restricted' && previewAccess !== 'unrestricted')
        || typeof value.papers_total_count !== 'number'
        || typeof value.hidden_count !== 'number'
        || (value.trial_path !== null && typeof value.trial_path !== 'string')
        || (previewAccess === 'restricted' && normalizedTrialPath === null)
        || (previewAccess === 'unrestricted' && value.trial_path !== null)
    ) {
        throw new Error('Paperzilla returned an invalid public preview access response.');
    }

    if (previewAccess === 'restricted' && visibleIds.length > 3) {
        throw new Error('Paperzilla returned an invalid public preview paper limit.');
    }

    const papersTotalCount = Math.max(0, Math.floor(value.papers_total_count));
    const hiddenCount = Math.max(0, Math.floor(value.hidden_count));
    if (
        (previewAccess === 'restricted'
            && (visibleIds.length > papersTotalCount
                || hiddenCount !== Math.max(papersTotalCount - visibleIds.length, 0)))
        || (previewAccess === 'unrestricted' && hiddenCount !== 0)
    ) {
        throw new Error('Paperzilla returned inconsistent public preview counts.');
    }

    return {
        digest_id: value.digest_id,
        project_id: value.project_id,
        preview_access: previewAccess,
        visible_project_paper_ids: visibleIds,
        papers_total_count: papersTotalCount,
        hidden_count: hiddenCount,
        trial_path: normalizedTrialPath,
    };
}

export async function fetchPublicDigestPreviewAccess(
    digestId: string,
    signal?: AbortSignal
): Promise<PublicDigestPreviewAccess> {
    const path = `/api/public/digests/${encodeURIComponent(digestId)}/preview-access`;
    const candidates = endpointCandidates(path);
    let lastError: Error | null = null;

    for (let index = 0; index < candidates.length; index += 1) {
        try {
            const response = await fetch(candidates[index], {
                method: 'GET',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
                signal,
            });

            if (!response.ok) {
                const canTryNext = index < candidates.length - 1
                    && (response.status === 404 || response.status === 405);
                if (canTryNext) {
                    lastError = new Error('Public preview access endpoint was not found.');
                    continue;
                }

                throw new Error(response.status === 404
                    ? 'Digest not found or unavailable.'
                    : 'Could not verify public preview access. Please reload and try again.');
            }

            return normalizePayload(await response.json(), digestId);
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                throw error;
            }

            lastError = error instanceof Error
                ? error
                : new Error('Could not verify public preview access. Please reload and try again.');
        }
    }

    throw lastError || new Error('Could not verify public preview access. Please reload and try again.');
}

function normalizeProjectPayload(
    payload: unknown,
    projectId: string
): PublicProjectPreviewAccess {
    const value = payload as Partial<PublicProjectPreviewAccess> | null;
    const previewAccess = value?.preview_access;
    const resultDigestId = value?.result_digest_id;
    const rawVisibleIds = value?.visible_project_paper_ids;
    const visibleIds = Array.isArray(rawVisibleIds)
        ? rawVisibleIds.filter((item): item is string => typeof item === 'string')
        : [];

    if (
        value?.project_id !== projectId
        || (previewAccess !== 'restricted' && previewAccess !== 'unrestricted')
        || (resultDigestId !== null && typeof resultDigestId !== 'string')
        || (typeof resultDigestId === 'string' && !resultDigestId.trim())
        || !Array.isArray(rawVisibleIds)
        || visibleIds.length !== rawVisibleIds.length
        || visibleIds.some((item) => !item.trim())
        || visibleIds.length > 3
        || new Set(visibleIds).size !== visibleIds.length
        || (previewAccess === 'unrestricted' && (resultDigestId !== null || visibleIds.length > 0))
        || (previewAccess === 'restricted' && resultDigestId === null && visibleIds.length > 0)
    ) {
        throw new Error('Paperzilla returned an invalid public project preview response.');
    }

    return {
        project_id: value.project_id,
        preview_access: previewAccess,
        result_digest_id: resultDigestId,
        visible_project_paper_ids: visibleIds,
    };
}

export async function fetchPublicProjectPreviewAccess(
    projectId: string,
    signal?: AbortSignal
): Promise<PublicProjectPreviewAccess> {
    const path = `/api/public/projects/${encodeURIComponent(projectId)}/preview-access`;
    const candidates = endpointCandidates(path);
    let lastError: Error | null = null;

    for (let index = 0; index < candidates.length; index += 1) {
        try {
            const response = await fetch(candidates[index], {
                method: 'GET',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
                signal,
            });

            if (!response.ok) {
                const canTryNext = index < candidates.length - 1
                    && (response.status === 404 || response.status === 405);
                if (canTryNext) {
                    lastError = new PublicProjectPreviewNotFoundError();
                    continue;
                }
                throw response.status === 404
                    ? new PublicProjectPreviewNotFoundError()
                    : new Error('Could not verify public project preview access. Please reload and try again.');
            }

            return normalizeProjectPayload(await response.json(), projectId);
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            lastError = error instanceof Error
                ? error
                : new Error('Could not verify public project preview access. Please reload and try again.');
        }
    }

    throw lastError
        || new Error('Could not verify public project preview access. Please reload and try again.');
}
